import {
  AlertColor,
  CSSObject,
  Theme,
  ThemeOptions,
  TypeBackground,
  alpha,
  darken,
  getContrastRatio,
  lighten,
  listItemSecondaryActionClasses,
  svgIconClasses,
} from '@mui/material';
import { blue, cyan, deepOrange, deepPurple, indigo, purple, teal } from '@mui/material/colors';
import { deepmerge } from '@mui/utils';

import { AssignmentType } from '../models';
import {
  ALERT_COLORS,
  COLOR_OPTIONS,
  CONTRAST_THRESHOLD,
  GRADIENT_COLORS,
  MUI_STATE_CLASSES,
} from './constants';
import { GradientBorderArgs, GradientConfig, GradientVariantArgs, MuiColor } from './types';

export const isMuiColor = (color: string | undefined): color is MuiColor =>
  color ? COLOR_OPTIONS.includes(color as MuiColor) : false;

export const isAlertColor = (color: string | undefined): color is AlertColor =>
  color ? ALERT_COLORS.includes(color as AlertColor) : false;

const getFocusVisibleStyles = (color: string): CSSObject => ({
  outline: 'none !important',
  boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${color} !important`,
});

const getContrastColor = (color: string | undefined, bgColor: string, theme: Theme) => {
  if (!color) {
    return theme.palette.getContrastText(bgColor);
  }

  const isContrasty =
    isMuiColor(color) && getContrastRatio(theme.palette[color].main, bgColor) >= CONTRAST_THRESHOLD;

  return isContrasty ? theme.palette[color].main : theme.palette.getContrastText(bgColor);
};

const getInvertedColorStyles = (theme: Theme, color: string | undefined): CSSObject => {
  const key = isMuiColor(color) ? color : 'primary';

  const bg = theme.palette[key].main;
  const fg = theme.palette.getContrastText(bg);

  return {
    [`&&.${MUI_STATE_CLASSES.selected}`]: {
      backgroundColor: bg,
      transition:
        'color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;',

      '&:hover': {
        backgroundColor: darken(bg, 0.12),
      },
    },
    [`&&.${MUI_STATE_CLASSES.selected},
      &&.${MUI_STATE_CLASSES.selected}+.${listItemSecondaryActionClasses.root},
      &&.${MUI_STATE_CLASSES.selected} *`]: {
      color: fg,

      [`& .${svgIconClasses.root}`]: {
        color: fg,
      },

      [`& .${MUI_STATE_CLASSES.focusVisible}`]: getFocusVisibleStyles(theme.palette.common.white),
    },
  };
};

const getGradientBg = ({ angle, color1, color2, bgOpacity = 1 }: GradientConfig): CSSObject => {
  const processedColor1 = bgOpacity === 1 ? color1 : alpha(color1, bgOpacity);
  const processedColor2 = bgOpacity === 1 ? color2 : alpha(color2, bgOpacity);

  return {
    backgroundImage: `linear-gradient(${angle}deg, ${processedColor1}, ${processedColor2})`,
    color: '#FFF',
  };
};

const getGradientBgWithHover = (config: GradientConfig): CSSObject => ({
  ...getGradientBg(config),
  transition: 'box-shadow 0.3s ease-in-out',

  '&:hover': {
    boxShadow: 'inset 0 0 0 2000px rgba(0, 0, 0, 0.30)',
  },
});

const getCommonGradient = ({
  variant,
  angle = 180,
  bgOpacity = 1,
  withHoverStyles = false,
}: GradientVariantArgs): CSSObject => {
  const colors = Array.isArray(variant)
    ? { color1: variant[0], color2: variant[1] }
    : GRADIENT_COLORS[variant];

  const gradientConfig: GradientConfig = { ...colors, angle, bgOpacity };

  return withHoverStyles ? getGradientBgWithHover(gradientConfig) : getGradientBg(gradientConfig);
};

const getGradientBorder = (args: GradientBorderArgs | undefined): CSSObject => {
  const { variant = 'light', width = 1, angle = 225, radius = 4 } = args || {};

  const { color1, color2 } = Array.isArray(variant)
    ? { color1: variant[0], color2: variant[1] }
    : GRADIENT_COLORS[variant];

  return {
    position: 'relative',
    border: '0px solid !important',
    borderRadius: `${radius}px`,

    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: `${radius}px`,
      padding: `${width}px`,
      background: `linear-gradient(${angle}deg, ${color1}, ${color2})`,
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      pointerEvents: 'none',
      zIndex: 1,
    },
  };
};

const getCompletedAssignmentStyles = (
  type: AssignmentType | undefined,
  withHoverStyles?: boolean
): CSSObject => {
  let styles: CSSObject;

  switch (type) {
    case AssignmentType.READING_COMPREHENSION:
      styles = {
        boxShadow: `inset 0 0 0 1px ${blue[900]}`,
        color: blue[900],
        ...(withHoverStyles && {
          '&:hover': {
            backgroundColor: blue[50],
          },
        }),
      };
      break;
    case AssignmentType.QUIZ:
      styles = {
        boxShadow: `inset 0 0 0 1px ${deepOrange[900]}`,
        color: deepOrange[900],
        ...(withHoverStyles && {
          '&:hover': {
            backgroundColor: deepOrange[50],
          },
        }),
      };
      break;
    case AssignmentType.STUDY_GUIDE:
    case AssignmentType.TUTOR:
      styles = {
        boxShadow: `inset 0 0 0 1px ${cyan[900]}`,
        color: cyan[900],
        ...(withHoverStyles && {
          '&:hover': {
            backgroundColor: cyan[50],
          },
        }),
      };
      break;
    case AssignmentType.TEST:
      styles = {
        boxShadow: `inset 0 0 0 1px #3f51b5`,
        color: '#3f51b5',
        ...(withHoverStyles && {
          '&:hover': {
            backgroundColor: indigo[50],
          },
        }),
      };
      break;
    case AssignmentType.TUTOR:
      styles = {
        boxShadow: `inset 0 0 0 1px ${teal[900]}`,
        color: teal[900],
        ...(withHoverStyles && {
          '&:hover': {
            backgroundColor: teal[50],
          },
        }),
      };
      break;
    case AssignmentType.DISCUSSION:
    default:
      styles = {
        boxShadow: `inset 0 0 0 1px ${deepPurple[900]}`,
        color: deepPurple[900],
        ...(withHoverStyles && {
          '&:hover': {
            backgroundColor: purple[50],
          },
        }),
      };
      break;
    case AssignmentType.TUTOR:
      styles = {
        boxShadow: `inset 0 0 0 1px ${teal[900]}`,
        color: teal[900],
        ...(withHoverStyles && {
          '&:hover': {
            backgroundColor: teal[50],
          },
        }),
      };
      break;
  }

  if (withHoverStyles) {
    return {
      ...styles,
      transition: 'background-color 0.3s ease-in-out',
    };
  }

  return styles;
};

export const augmentTheme = (theme: Theme): Theme =>
  deepmerge(theme, {
    palette: {
      background: ALERT_COLORS.reduce(
        (acc, key) => ({
          ...acc,
          [key]: lighten(theme.palette[key].light, 0.9),
        }),
        {} satisfies Partial<TypeBackground>
      ),
    },
    mixins: {
      focusVisible: (color: string) => getFocusVisibleStyles(color),
      contrastColor: (color: string | undefined, bgColor: string) =>
        getContrastColor(color, bgColor, theme),
      invertedColor: (color?: string) => getInvertedColorStyles(theme, color),
      commonGradient: getCommonGradient,
      assignmentGradient: (args) =>
        args.isCompleted
          ? getCompletedAssignmentStyles(args.type, args.withHoverStyles)
          : getCommonGradient({
              variant: args.type ?? 'blue',
              angle: 135,
              withHoverStyles: args.withHoverStyles,
            }),
      gradientBorder: getGradientBorder,
    },
  } satisfies ThemeOptions);

export const getFilledIconButtonStyles = (theme: Theme, color?: MuiColor): CSSObject => ({
  backgroundColor: color ? theme.palette[color].main : theme.palette.grey[300],
  color: theme.palette.getContrastText(color ? theme.palette[color].main : theme.palette.divider),
  boxShadow: theme.shadows[1],

  '&:hover': {
    backgroundColor: color ? theme.palette[color].dark : theme.palette.action.hover,
  },
});

export const getOutlinedIconButtonStyles = (theme: Theme, color?: MuiColor): CSSObject => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.getContrastText(theme.palette.background.paper),
  border: `1px solid ${color ? theme.palette[color].light : theme.palette.divider}`,

  [`& .${svgIconClasses.root}`]: {
    margin: '-1px', // Account for 1px border
  },

  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
});
